/**
 * Iter 208 PART A — Personnel GMP Suitability modal.
 *
 * Wraps the Iter207 backend at /api/personnel/suitability.
 * - Statuses: SUITABLE / NOT_SUITABLE / PENDING_REVIEW  (§22 — no clinical data)
 * - Never captures any protected personal health information.
 * - "Beni Ata" — current session user is snapshotted server-side (assessed_by).
 * - External evidence remains a reference to gmp_documents; not a Master Document.
 * - PDF is preview-first via openPdfPreview (Iter164 dialog).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Select, Input, Textarea, Badge } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { ShieldCheck, FileText, Trash2, Paperclip } from "lucide-react";
import { todayTR } from "@/lib/date";

const STATUS_OPTS = [
  { v: "SUITABLE",       label: "UYGUN",           tone: "ok"   },
  { v: "PENDING_REVIEW", label: "GÖZDEN GEÇİRME",  tone: "warn" },
  { v: "NOT_SUITABLE",   label: "UYGUN DEĞİL",     tone: "err"  },
];

const toneOf = (s) => STATUS_OPTS.find(o => o.v === s)?.tone || "warn";
const labelOf = (s) => STATUS_OPTS.find(o => o.v === s)?.label || s;

export default function SuitabilityModal({ employee, open, onClose }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [evidenceDocs, setEvidenceDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    status: "SUITABLE",
    assessment_date: todayTR(),
    review_date: "",
    external_evidence_document_id: "",
    operational_note: "",
  });

  const load = useCallback(async () => {
    if (!employee?.id) return;
    try {
      const r = await api.get(`/personnel/suitability?employee_id=${employee.id}`);
      setRows(Array.isArray(r) ? r : []);
    } catch { setRows([]); }
  }, [employee?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // §14 external evidence is a reference to gmp_documents, not a new master doc.
  useEffect(() => {
    if (!open) return;
    api.get("/gmp/documents").then(r => {
      const items = Array.isArray(r) ? r : (r?.items || []);
      setEvidenceDocs(items);
    }).catch(() => setEvidenceDocs([]));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/personnel/suitability", {
        employee_id: employee.id,
        status: form.status,
        assessment_date: form.assessment_date,
        review_date: form.review_date || null,
        external_evidence_document_id: form.external_evidence_document_id || null,
        operational_note: form.operational_note || null,
      });
      setForm({
        status: "SUITABLE",
        assessment_date: todayTR(),
        review_date: "",
        external_evidence_document_id: "",
        operational_note: "",
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const dlPdf = async (id) => {
    try {
      const blob = await api.getBlob(`/personnel/suitability/${id}/pdf`);
      openPdfPreview(blob, `Uygunluk_${employee?.first_name || ""}_${id.slice(0, 6)}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  const del = async (id) => {
    if (!confirm("Uygunluk kaydı silinsin mi?")) return;
    await api.delete(`/personnel/suitability/${id}`);
    load();
  };

  if (!employee) return null;

  const current = rows[0]; // list is sorted assessment_date DESC — first is latest

  return (
    <Modal open={open} onClose={onClose}
      title={`GMP Uygunluk — ${employee.first_name} ${employee.last_name}`} size="lg">
      {/* §12 no-clinical-data notice */}
      <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sky-900" data-testid="psu-notice">
        Operasyonel GMP uygunluk izlenebilirliği. Bu form yalnızca operasyonel duruma yöneliktir;
        koruma altındaki kişisel sağlık bilgileri kaydedilmez.
        Değerlendiren: <b>{user?.name || user?.username}</b> (Beni Ata — mevcut oturum).
      </div>

      {/* Current status snapshot */}
      {current ? (
        <div className="mb-3 border border-slate-200 rounded p-3 bg-slate-50/60" data-testid="psu-current">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Güncel Durum</div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Badge level={toneOf(current.status)} data-testid="psu-current-status">{labelOf(current.status)}</Badge>
            <div><span className="text-slate-500">Değerlendirme:</span> <span className="font-mono">{current.assessment_date}</span></div>
            <div><span className="text-slate-500">Gözden Geçirme:</span> <span className="font-mono">{current.review_date || "—"}</span></div>
            <div><span className="text-slate-500">Değerlendiren:</span> {current.assessed_by_name || "—"}</div>
            {current.external_evidence_document_id && (
              <div className="flex items-center gap-1 text-slate-600">
                <Paperclip size={11}/> Kanıt: <span className="font-mono">{current.external_evidence_document_id.slice(0, 8)}</span>
              </div>
            )}
          </div>
          {current.operational_note && (
            <div className="text-xs text-slate-700 mt-2 italic">Not: {current.operational_note}</div>
          )}
        </div>
      ) : (
        <div className="text-xs italic text-slate-400 border border-dashed border-slate-200 rounded p-2 mb-3" data-testid="psu-empty">
          Bu personel için henüz uygunluk değerlendirmesi yok.
        </div>
      )}

      {/* New assessment / review */}
      <div className="mb-4 border border-slate-200 rounded p-3 bg-white" data-testid="psu-new">
        <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Yeni Değerlendirme / Gözden Geçirme</div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Durum *" value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })} data-testid="psu-status">
            {STATUS_OPTS.map(o => (<option key={o.v} value={o.v}>{o.label}</option>))}
          </Select>
          <Input type="date" label="Değerlendirme Tarihi *" value={form.assessment_date}
            onChange={e => setForm({ ...form, assessment_date: e.target.value })} data-testid="psu-date"/>
          <Input type="date" label="Gözden Geçirme Tarihi" value={form.review_date}
            onChange={e => setForm({ ...form, review_date: e.target.value })} data-testid="psu-review"/>
          <Select label="Destekleyici Kanıt (Belge)" value={form.external_evidence_document_id}
            onChange={e => setForm({ ...form, external_evidence_document_id: e.target.value })}
            data-testid="psu-evidence">
            <option value="">— Yok —</option>
            {evidenceDocs.map(d => (
              <option key={d.id} value={d.id}>
                {(d.title || d.document_name || d.id).slice(0, 40)}
              </option>
            ))}
          </Select>
        </div>
        <Textarea label="Operasyonel Not" rows={2} value={form.operational_note}
          onChange={e => setForm({ ...form, operational_note: e.target.value })}
          placeholder="Sadece operasyonel/GMP not (klinik veri girmeyin)."
          data-testid="psu-note"/>
        <div className="flex justify-end mt-2">
          <Btn onClick={save} disabled={busy} data-testid="psu-save">
            <ShieldCheck size={12}/> Kaydet
          </Btn>
        </div>
      </div>

      {/* History */}
      <div>
        <div className="text-xs font-semibold uppercase text-slate-600 mb-1" data-testid="psu-history-title">
          Geçmiş ({rows.length})
        </div>
        {rows.length === 0 && (
          <div className="text-xs italic text-slate-400 py-2">Kayıt yok.</div>
        )}
        {rows.map(r => (
          <div key={r.id}
            className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs"
            data-testid={`psu-row-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono">{r.assessment_date}</span>
              <Badge level={toneOf(r.status)}>{labelOf(r.status)}</Badge>
              {r.review_date && <span className="text-slate-500">Gözden Geçirme: <span className="font-mono">{r.review_date}</span></span>}
              <span className="text-slate-500">Değerlendiren: {r.assessed_by_name || "—"}</span>
              {r.external_evidence_document_id && (
                <span className="text-slate-500 flex items-center gap-1"><Paperclip size={10}/> kanıt</span>
              )}
            </div>
            <div className="flex gap-1">
              <Btn size="sm" variant="ghost" onClick={() => dlPdf(r.id)} data-testid={`psu-pdf-${r.id}`}>
                <FileText size={11}/> PDF
              </Btn>
              <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`psu-del-${r.id}`}>
                <Trash2 size={11}/>
              </Btn>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
